






import { defineComponent, ref, onMounted, onUnmounted, watch } from "@vue/composition-api";
import { InputFileId } from "@scrile/api-provider/dist/api/FileProvider";
import useFiles from "@/hooks/useFiles";
import useCaptureSnapshot from "./useCaptureSnapshot";

export default defineComponent({
  name: "StreamSnapshotController",
  props: {
    stream: { type: MediaStream, required: true },
    allowSnapshot: { type: Boolean, default: true },
    userId: { type: String, required: true },
  },
  setup(props) {
    const SNAPSHOT_INTERVAL = 15000;
    const canvasRef = ref<HTMLCanvasElement | null>(null);
    const videoRef = ref<HTMLVideoElement | null>(null);
    let interval: ReturnType<typeof setInterval>;
    const { uploadThenUpdate } = useFiles();
    const { captureSnapshot } = useCaptureSnapshot(canvasRef, videoRef);

    const setupVideo = async () => {
      if (!videoRef.value || !props.stream) return;
      videoRef.value.srcObject = props.stream;

      return new Promise<void>(resolve => {
        if (!videoRef.value) return;

        const onLoadedData = () => {
          if (!videoRef.value) return;
          videoRef.value.play();
          videoRef.value.removeEventListener("loadeddata", onLoadedData);
          resolve();
        };

        videoRef.value.addEventListener("loadeddata", onLoadedData);
      });
    };

    const uploadSnapshot = async () => {
      if (!props.allowSnapshot) return;

      const snapshotFile = await captureSnapshot();
      if (!snapshotFile) return;

      const fileId: InputFileId = {
        subjectId: props.userId,
        type: "livepreview_snapshot",
      };

      await uploadThenUpdate({
        fileId,
        file: snapshotFile,
      });
    };

    const stopSnapshotInterval = () => {
      if (interval) {
        clearInterval(interval);
      }
    };

    const startSnapshotInterval = () => {
      stopSnapshotInterval();
      interval = setInterval(uploadSnapshot, SNAPSHOT_INTERVAL);
    };

    watch(
      () => props.allowSnapshot,
      newValue => {
        if (newValue) {
          startSnapshotInterval();
        } else {
          stopSnapshotInterval();
        }
      }
    );

    watch(
      () => props.stream,
      async newStream => {
        if (newStream) {
          await setupVideo();
          if (props.allowSnapshot) {
            startSnapshotInterval();
          }
        } else {
          stopSnapshotInterval();
        }
      }
    );

    onMounted(async () => {
      if (props.stream) {
        await setupVideo();
        if (props.allowSnapshot) {
          startSnapshotInterval();
        }
      }
    });

    onUnmounted(() => {
      stopSnapshotInterval();
      if (videoRef.value) {
        videoRef.value.srcObject = null;
      }
    });

    return {
      canvasRef,
      videoRef,
    };
  },
});
